/**
 * PR-36E — Experience Mode Engine.
 * Dual experience modes: Emotional (default) vs Pro Analytics.
 * Persisted in localStorage. Pure module, demo-safe.
 */
import { trackEvent } from "@/lib/audit";

export type ExperienceMode = "emotional" | "pro";

const STORAGE_KEY = "vs.experienceMode";
const PRO_DWELL_KEY = "vs.experienceMode.proDwellMs";
const HYBRID_SUGGEST_MS = 30 * 60_000; // 30 min in Pro → suggest hybrid

type Listener = (m: ExperienceMode) => void;
const listeners = new Set<Listener>();
let current: ExperienceMode | null = null;
let proEnterAt = 0;

function read(): ExperienceMode {
  try {
    const v = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    return v === "pro" ? "pro" : "emotional";
  } catch { return "emotional"; }
}

export function getExperienceMode(): ExperienceMode {
  if (current == null) current = read();
  return current;
}

export function setExperienceMode(mode: ExperienceMode): void {
  const prev = getExperienceMode();
  if (prev === mode) return;
  current = mode;
  try { window.localStorage.setItem(STORAGE_KEY, mode); } catch { /* noop */ }
  if (mode === "pro") proEnterAt = Date.now();
  else if (prev === "pro" && proEnterAt > 0) {
    const dwell = Date.now() - proEnterAt;
    try {
      const acc = Number(window.localStorage.getItem(PRO_DWELL_KEY) ?? "0") + dwell;
      window.localStorage.setItem(PRO_DWELL_KEY, String(acc));
    } catch { /* noop */ }
    proEnterAt = 0;
  }
  void trackEvent("experience_mode_changed", { mode, prev });
  listeners.forEach((l) => l(mode));
}

export function toggleExperienceMode(): ExperienceMode {
  const next: ExperienceMode = getExperienceMode() === "pro" ? "emotional" : "pro";
  setExperienceMode(next);
  return next;
}

export function subscribeExperienceMode(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

export function isEmotionalMode(): boolean { return getExperienceMode() === "emotional"; }
export function isProMode(): boolean { return getExperienceMode() === "pro"; }

/** Cumulative time the user has spent in Pro Mode (ms). */
export function getProDwellMs(): number {
  try { return Number(window.localStorage.getItem(PRO_DWELL_KEY) ?? "0"); }
  catch { return 0; }
}

/** Hybrid suggestion: once user has logged enough Pro time, suggest deeper metrics. */
export function shouldSuggestHybrid(): boolean {
  const dwell = getProDwellMs() + (getExperienceMode() === "pro" && proEnterAt ? Date.now() - proEnterAt : 0);
  const suggest = dwell >= HYBRID_SUGGEST_MS;
  if (suggest) void trackEvent("experience_hybrid_suggested", { dwell_ms: dwell });
  return suggest;
}

/* ───────────────────────── Pro Analytics presentation ───────────────────────── */

export type ProMetricKey =
  | "xg" | "offensive_pressure" | "fatigue" | "intensity"
  | "goal_probability" | "momentum" | "vulnerable_zones"
  | "tactical_maps" | "defensive_risk" | "discipline" | "recent_form";

export interface ProMetric {
  key: ProMetricKey;
  label: string;
  value: string;     // e.g. "+0.42", "84%"
  trend: "up" | "down" | "flat";
  emoji: string;
}

export const PRO_METRIC_LIBRARY: Record<ProMetricKey, { label: string; emoji: string }> = {
  xg:                  { label: "xG incremental",       emoji: "🎯" },
  offensive_pressure:  { label: "Presión ofensiva",     emoji: "🔥" },
  fatigue:             { label: "Fatiga defensiva",     emoji: "😮‍💨" },
  intensity:           { label: "Intensidad",           emoji: "⚡" },
  goal_probability:    { label: "Probabilidad de gol",  emoji: "🥅" },
  momentum:            { label: "Momentum",             emoji: "📈" },
  vulnerable_zones:    { label: "Zonas vulnerables",    emoji: "🗺️" },
  tactical_maps:       { label: "Mapas tácticos",       emoji: "🧭" },
  defensive_risk:      { label: "Riesgo defensivo",     emoji: "🛡️" },
  discipline:          { label: "Disciplina",           emoji: "🟨" },
  recent_form:         { label: "Rendimiento reciente", emoji: "📊" },
};

/** Smart presentation rule: never show more than this in Pro Mode at once. */
export const PRO_MAX_VISIBLE_METRICS = 2;
export const PRO_AUTO_DISMISS_MS = 6_000;

export function selectProMetrics(all: ProMetric[]): ProMetric[] {
  // Highest absolute trend first; keep cap.
  const score = (m: ProMetric) => (m.trend === "flat" ? 0 : 1);
  return [...all].sort((a, b) => score(b) - score(a)).slice(0, PRO_MAX_VISIBLE_METRICS);
}

/* ───────────────────────── Predictive credibility ───────────────────────── */

export interface CredibilityFactor {
  key: string;
  label: string;
  weight: number; // 0..1
}

export interface CredibilityExplanation {
  prediction: string;
  confidence: number; // 0..1
  factors: CredibilityFactor[];
  summary: string;
}

export function buildCredibilityExplanation(input: {
  prediction: string;
  confidence: number;
  factors: CredibilityFactor[];
}): CredibilityExplanation {
  const top = [...input.factors].sort((a, b) => b.weight - a.weight).slice(0, 4);
  const summary = `Basado en: ${top.map((f) => f.label.toLowerCase()).join(", ")}.`;
  void trackEvent("predictive_credibility_explained", {
    prediction: input.prediction,
    confidence: input.confidence,
    factors: top.map((f) => f.key),
  });
  return { ...input, factors: top, summary };
}

/* ───────────────────────── Community Pro insights ───────────────────────── */

export interface CommunityProInsight {
  text: string;
  agreementPct: number; // 0..100
  metric: ProMetricKey;
}

export function buildCommunityProInsight(metric: ProMetricKey, agreementPct: number): CommunityProInsight {
  const m = PRO_METRIC_LIBRARY[metric];
  const text = `${Math.round(agreementPct)}% de usuarios PRO detectan ${m.label.toLowerCase()}.`;
  void trackEvent("community_pro_insight_emitted", { metric, agreement_pct: agreementPct });
  return { text, agreementPct, metric };
}